// PlacementConfigSubRow (UX-16, editing added UX-17.8) — a placement's
// configuration, one parameter per line, as the expander under its grid row.
//
// UX-17.8 (Alex, 2026-08-01): *"can we get rid of the configuration on the right
// side and directly configure a placement in that extended row?"* — so expanding a
// row IS configuring it. There is no separate edit mode: the controls are live, and
// a Save / Revert bar appears only once that row is dirty. A frozen (purchased)
// placement stays read-only, same rule the right-hand panel used.
//
// Why every parameter and not just the overridden ones (Alex, 2026-08-01: "Typical
// or not typical, the parameters must be there"): actual_values is SPARSE — a
// parameter left at its typical is simply absent from it — so showing only what is
// stored makes two differently-resolving rows look identically blank.
//
// Overridden vs typical is carried by WEIGHT plus the word "typical", never colour
// (Alex is colour-blind).
//
// Props:
//   params        — resolved.params: { name, type, unit, value, text, typical, overridden,
//                   min, max, size_unit, options, description }
//   orphanParams  — resolved.orphan_params: { name, value } stored under a name the
//                   product does not declare, so it resolves to nothing at all.
//   values        — this row's edit buffer, { [name]: value }; absent keys fall back to param.value
//   onChange      — (name, value) => void. Omit (with onSave) for a read-only expander.
//   onSave/onRevert, dirty, saving
//   sizeListOptionsByUnit — { [size_unit]: [{value,label,nps}] }, prefetched by the consumer
//   readOnly      — frozen placement: show values, never controls
//   indent        — px from the sub-row's left edge to start at. The consumer passes
//                   DataGrid's offsetOf('config') so the block lines up under the
//                   Configuration button and follows a column resize.

import { WARN } from '../icons.js';
import SelectCell from './SelectCell.jsx';
import FormulaCell from './FormulaCell.jsx';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DISABLED } from '../theme-tokens.js';

const NUM = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 });

// Exported as formatParamValue: a PARAMETER VALUE is not a quantity. formatQty is
// deliberately 0-decimal ("comma thousands, 0 decimals"), which is right for 5,470 kg
// and wrong for a config value — the Product mapping preview was reporting that 0.75
// "resolves to 1". Both screens now format parameter values the same way.
export function formatParamValue(v) { return fmt(v); }

function fmt(v) {
  if (v == null || v === '') return '—';
  return typeof v === 'number' ? NUM.format(v) : String(v);
}

// A list param stores the numeric size; the standard size list supplies the text
// (server-side, via size_lists). Fall back to the raw number if the pick is unknown
// to the list rather than rendering nothing.
function readValue(p, v) {
  if (v === p.value && p.text) return p.text;
  return fmt(v);
}

// Mirrors ParameterPanel's list semantics exactly — a size-unit list narrows to the
// param's min/max nps window and stores the NUMERIC nps; a custom list stores its
// own option value as-is.
function listOptions(p, sizeListOptionsByUnit) {
  const isSizeUnit = p.size_unit && p.size_unit !== 'custom';
  if (!isSizeUnit) {
    try { const a = JSON.parse(p.options || '[]'); return { isSizeUnit, opts: Array.isArray(a) ? a : [] }; }
    catch { return { isSizeUnit, opts: [] }; }
  }
  const lo = (p.min != null && Number.isFinite(Number(p.min))) ? Number(p.min) : null;
  const hi = (p.max != null && Number.isFinite(Number(p.max))) ? Number(p.max) : null;
  const opts = (sizeListOptionsByUnit[p.size_unit] || []).filter(o =>
    o.num == null ? true : ((lo == null || Number(o.num) >= lo) && (hi == null || Number(o.num) <= hi)));
  return { isSizeUnit, opts };
}

const NAME_STYLE = { minWidth: 96, flexShrink: 0, fontSize: 'var(--fs-small)' };
const CTRL_BORDER = atTypical => `1px solid ${atTypical ? 'var(--th-border-subtle)' : 'var(--th-warn-accent)'}`;

// EXPORTED so the Product screen's component-parameter list is the SAME component,
// not a lookalike (Alex, 2026-08-04: "I would like you to consider updating product
// like function... simply doing the look"). Two screens drawing one concept twice is
// how they drift; the Labour picker and the cascade both got here that way.
//
// The Product screen differs in exactly one respect, and `allowFormula` is it. On a
// placement you are answering "what value?", so a number box is right. On a component
// you are answering the same question one level earlier, where the answer may be
// "whatever the PARENT's Weight is × 1.2" — so the same box also accepts an expression.
// Alex's own framing, 2026-08-04: "typical is the default value. Then, or it keeps that
// value, or I type something else, or I type a formula." One box, three outcomes, no
// mode to pick first — which is why the old ⚑ typical / ⇐ link dropdown is gone.
//
//   atTypicalOverride — Product cannot use `value === p.typical`: "typical" there means
//                       the param_map has NO ENTRY, and an entry that happens to equal
//                       the typical is still an explicit override.
//   after             — trailing content (Product shows what the formula resolves to,
//                       which is the only proof it evaluates at all).
//   onClearToTypical  — Product only. A checkbox or a dropdown cannot express "leave
//                       this at the typical" the way an empty text box can, so when a
//                       screen HAS that third state it supplies this and gets a reset
//                       control beside the value. Function never passes it: a placement
//                       always holds a value.
export function ParamLine({ p, value, onChange, sizeListOptionsByUnit, readOnly,
                            allowFormula = false, atTypicalOverride, after, onClearToTypical, nameWidth }) {
  const atTypical = atTypicalOverride !== undefined ? atTypicalOverride : value === p.typical;
  const editable = !readOnly && typeof onChange === 'function';

  let control;
  if (!editable) {
    control = (
      <span style={{
        fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', fontVariantNumeric: 'tabular-nums',
        fontWeight: atTypical ? 400 : 600,
        color: atTypical ? 'var(--th-text-secondary)' : 'var(--th-text-body)',
      }}>
        {readValue(p, value)}{p.unit ? ` ${p.unit}` : ''}
      </span>
    );
  } else if (allowFormula) {
    // Number OR expression, decided by what is typed — the same rule the Qty cell
    // already uses. Empty means "leave it at the typical", so clearing the box is how
    // you go back, and there is no separate control that has to agree with it.
    control = (
      <span style={{ width: 170, display: 'inline-block' }}>
        <FormulaCell
          value={value ?? ''}
          onChange={v => onChange(p.name, v)}
          placeholder={p.typical != null ? `typical: ${fmt(p.typical)}` : 'typical'}
          title={`A number, or a formula over the parent's parameters — sets ${p.name}. Empty leaves it at the sub-product's typical.`}
        />
      </span>
    );
  } else if (p.type === 'list') {
    const { isSizeUnit, opts } = listOptions(p, sizeListOptionsByUnit);
    // SelectCell also preserves an off-list current value, which matters here: a
    // size-unit list is narrowed to the param's min/max nps window, so a stored size
    // outside that window must still show rather than silently reading as another one.
    control = (
      <span style={{ minWidth: 150, display: 'inline-block' }}>
        <SelectCell
          value={String(value ?? '')}
          options={opts.map(o => ({ value: isSizeUnit ? String(o.num) : String(o.value), label: o.label }))}
          onChange={raw => {
            if (isSizeUnit) { onChange(p.name, Number(raw)); return; }
            const o = opts.find(x => String(x.value) === raw);
            onChange(p.name, o ? o.value : raw);
          }}
        />
      </span>
    );
  } else if (p.type === 'boolean') {
    control = (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 'var(--fs-small)' }}>
        <input
          type="checkbox"
          checked={Number(value) === 1}
          onChange={e => onChange(p.name, e.target.checked ? 1 : 0)}
        />
        {Number(value) === 1 ? 'Yes' : 'No'}
      </label>
    );
  } else {
    control = (
      <input
        type="number"
        value={value ?? ''}
        onChange={e => {
          const t = e.target.value;
          onChange(p.name, t === '' ? '' : Number(t));
        }}
        style={{
          width: 90, fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)',
          fontVariantNumeric: 'tabular-nums', textAlign: 'right', padding: '1px 4px',
          border: CTRL_BORDER(atTypical), borderRadius: 3, background: 'var(--th-row-even)',
        }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 22 }}>
      <span style={{ ...NAME_STYLE, ...(nameWidth ? { minWidth: nameWidth, width: nameWidth } : {}), color: 'var(--th-text-secondary)' }}
            title={p.description || undefined}>
        {p.name}
      </span>
      {control}
      {editable && p.unit && p.type !== 'list' && p.type !== 'boolean' && (
        <span style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>{p.unit}</span>
      )}
      {atTypical && (
        <span style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-hint)' }}>typical</span>
      )}
      {onClearToTypical && !atTypical && editable && (
        <button
          onClick={() => onClearToTypical(p.name)}
          title={`Back to the typical${p.typical != null ? ` (${fmt(p.typical)})` : ''}`}
          // ↺ so this cannot be read as the "typical" MARKER beside it — one is a
          // statement (it IS at the typical), the other an action (put it back).

          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
            fontSize: 'var(--fs-micro)', color: 'var(--th-text-secondary)', textDecoration: 'underline',
          }}
        >↺ typical</button>
      )}
      {after}
    </div>
  );
}

export default function PlacementConfigSubRow({
  params = [], orphanParams = [], values = {},
  onChange, onSave, onRevert, dirty, saving,
  sizeListOptionsByUnit = {}, readOnly, indent = 40,
}) {
  if (!params.length && !orphanParams.length) return null;
  const valueOf = p => (p.name in values ? values[p.name] : p.value);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 2,
      padding: '7px 12px 7px 0',
      paddingInlineStart: indent,
      borderTop: '1px dashed var(--th-border-light)',
      background: 'var(--th-row-even)',
    }}>
      {params.map(p => (
        <ParamLine
          key={p.name}
          p={p}
          value={valueOf(p)}
          onChange={onChange}
          sizeListOptionsByUnit={sizeListOptionsByUnit}
          readOnly={readOnly}
        />
      ))}
      {orphanParams.map(p => (
        <div key={`orphan:${p.name}`} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 22 }}>
          <span style={{ ...NAME_STYLE, color: 'var(--th-text-body)' }}>{WARN} {p.name}</span>
          <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)' }}>{fmt(p.value)}</span>
          <span style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-hint)' }}>
            stored here, but this product declares no such parameter — it has no effect
          </span>
        </div>
      ))}
      {readOnly && (
        <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-hint)', marginTop: 3 }}>
          Purchased — configuration frozen with its price.
        </div>
      )}
      {dirty && !readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
          <button
            onClick={onSave}
            disabled={saving}
            style={{ ...BTN_PRIMARY, fontWeight: 600, ...(saving ? BTN_DISABLED : null) }}
          >{saving ? 'Saving…' : 'Save'}</button>
          <button
            onClick={onRevert}
            disabled={saving}
            style={{ ...BTN_SECONDARY, ...(saving ? BTN_DISABLED : null) }}
          >Revert</button>
          <span style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-warn-accent)' }}>unsaved</span>
        </div>
      )}
    </div>
  );
}
